<script>
/* global axios */
// [AstraChat] Canal NotificaMe (provedor virtual, token GLOBAL). Lista os canais do
// pool da revenda (list_channels) e conecta os atribuídos à empresa — SEM QR. Além
// disso, quando o company_uuid está configurado, permite CRIAR/conectar um canal NOVO
// na hora via popup Seamless (OAuth do provedor) — o canal é auto-atribuído à empresa.
// Ver notificame-integration-spec (§9 + Seamless).
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: { PageHeader, NextButton },
  data() {
    return {
      loading: true,
      error: '',
      channels: [],
      connectingId: null,
      brandName: 'NotificaMe',
      subtitle: '',
      seamlessAvailable: false,
      seamlessTypes: [],
      seamlessBusy: false,
    };
  },
  computed: {
    ...mapGetters({ accountId: 'getCurrentAccountId' }),
    baseUrl() {
      return `/api/v1/accounts/${this.accountId}/kanban/connections/notificame`;
    },
  },
  mounted() {
    this.loadChannels();
  },
  methods: {
    async loadChannels() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await axios.get(`${this.baseUrl}/channels`);
        this.channels = data.data || [];
        this.brandName = data.brand_name || 'NotificaMe';
        this.subtitle = data.subtitle || '';
        this.seamlessAvailable = data.seamless_available || false;
        this.seamlessTypes = data.seamless_types || [];
      } catch (e) {
        this.error =
          e?.response?.data?.error ||
          `Não foi possível listar os canais do ${this.brandName}.`;
      } finally {
        this.loading = false;
      }
    },
    channelBadge(ch) {
      if (ch.connected) return { label: 'Conectado', cls: 'bg-n-teal-3 text-n-teal-11' };
      if (!ch.allowed) return { label: 'Não atribuído', cls: 'bg-n-alpha-2 text-n-slate-11' };
      if (ch.quota_reached) return { label: 'Limite atingido', cls: 'bg-n-ruby-3 text-n-ruby-11' };
      return { label: 'Disponível', cls: 'bg-n-amber-3 text-n-amber-11' };
    },
    async connect(ch) {
      if (!ch.allowed || ch.connected || ch.quota_reached) return;
      this.connectingId = ch.id;
      try {
        const { data } = await axios.post(`${this.baseUrl}/connect`, {
          channel_id: ch.id,
          channel_type: ch.channel,
          channel_name: ch.name,
        });
        useAlert(`Canal conectado.`);
        router.replace({
          name: 'settings_inbox_show',
          params: { inboxId: data.inbox_id },
        });
      } catch (e) {
        useAlert(
          e?.response?.data?.error || `Falha ao conectar o canal.`
        );
      } finally {
        this.connectingId = null;
      }
    },
    // [AstraChat] Seamless: abre o popup OAuth, espera o postMessage channel-success
    // e finaliza (o backend descobre o canal novo, atribui e cria a inbox).
    async openSeamless(type) {
      if (this.seamlessBusy) return;
      this.seamlessBusy = true;
      // Abre o popup JÁ no clique (síncrono) p/ não cair no bloqueador; a URL entra depois.
      const popup = window.open('', 'notificameConnect', 'width=800,height=900');
      try {
        const { data } = await axios.post(`${this.baseUrl}/seamless_start`, {
          channel_type: type,
        });
        if (!popup || popup.closed) {
          useAlert('Permita pop-ups para este site e tente novamente.');
          this.seamlessBusy = false;
          return;
        }
        popup.location = data.url;
        const beforeIds = data.before_ids || [];
        const expectedOrigin = data.origin;

        const onMessage = ev => {
          if (expectedOrigin && ev.origin !== expectedOrigin) return;
          const status =
            (ev.data && (ev.data.status || ev.data.type)) ||
            (typeof ev.data === 'string' ? ev.data : null);
          if (status !== 'channel-success') return;
          window.removeEventListener('message', onMessage);
          try {
            popup.close();
          } catch (_e) {
            /* noop */
          }
          this.finishSeamless(type, beforeIds);
        };
        window.addEventListener('message', onMessage);

        // Segurança: se o usuário fechar o popup sem concluir, libera o botão.
        const watchClose = setInterval(() => {
          if (popup.closed) {
            clearInterval(watchClose);
            window.removeEventListener('message', onMessage);
            if (this.seamlessBusy) this.seamlessBusy = false;
          }
        }, 1000);
      } catch (e) {
        try {
          if (popup) popup.close();
        } catch (_e) {
          /* noop */
        }
        useAlert(
          e?.response?.data?.error || 'Não foi possível iniciar a conexão.'
        );
        this.seamlessBusy = false;
      }
    },
    async finishSeamless(type, beforeIds, attempt = 0) {
      try {
        const { data } = await axios.post(`${this.baseUrl}/seamless_finish`, {
          channel_type: type,
          before_ids: beforeIds,
        });
        this.seamlessBusy = false;
        useAlert('Canal conectado.');
        router.replace({
          name: 'settings_inbox_show',
          params: { inboxId: data.inbox_id },
        });
      } catch (e) {
        // O canal pode demorar alguns segundos p/ aparecer no pool — re-tenta.
        if (e?.response?.data?.retry && attempt < 6) {
          setTimeout(() => this.finishSeamless(type, beforeIds, attempt + 1), 2500);
          return;
        }
        this.seamlessBusy = false;
        useAlert(
          e?.response?.data?.error || 'Falha ao finalizar a conexão do canal.'
        );
        this.loadChannels();
      }
    },
    openInbox(ch) {
      if (!ch.inbox_id) return;
      router.push({ name: 'settings_inbox_show', params: { inboxId: ch.inbox_id } });
    },
  },
};
</script>

<template>
  <div class="w-full h-full col-span-6 p-6">
    <PageHeader :header-title="brandName" :header-content="subtitle" />

    <div v-if="loading" class="py-6 text-n-slate-11">Carregando canais…</div>

    <div
      v-else-if="error"
      class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 p-4 text-n-ruby-11"
    >
      {{ error }}
      <button class="ms-2 underline" @click="loadChannels">tentar de novo</button>
    </div>

    <template v-else>
      <!-- Conectar novo canal (Seamless): só quando o company_uuid está configurado. -->
      <div
        v-if="seamlessAvailable && seamlessTypes.length"
        class="mt-2 mb-4 max-w-2xl rounded-lg border border-n-weak p-3"
      >
        <div class="text-sm font-medium text-n-slate-12">Conectar novo canal</div>
        <div class="text-xs text-n-slate-10 mb-2.5">
          A conexão abre em uma janela e é imediata.
        </div>
        <div class="flex flex-wrap gap-2">
          <NextButton
            v-for="opt in seamlessTypes"
            :key="opt.type"
            solid
            blue
            :label="opt.label"
            :disabled="seamlessBusy"
            :is-loading="seamlessBusy"
            @click="openSeamless(opt.type)"
          />
        </div>
      </div>

      <div v-if="!channels.length" class="py-4 text-n-slate-11">
        Nenhum canal conectado ainda.
        <span v-if="seamlessAvailable && seamlessTypes.length"
          >Use "Conectar novo canal" acima.</span
        >
      </div>

      <ul v-else class="mt-2 flex flex-col gap-2 max-w-2xl">
        <li
          v-for="ch in channels"
          :key="ch.id"
          class="flex items-center justify-between gap-3 rounded-lg border border-n-weak p-3"
        >
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <span class="font-medium text-n-slate-12 truncate">{{ ch.name }}</span>
              <span
                class="rounded px-1.5 py-0.5 text-xs"
                :class="channelBadge(ch).cls"
              >{{ channelBadge(ch).label }}</span>
            </div>
            <div class="text-xs text-n-slate-10 truncate">{{ ch.channel }} · {{ ch.id }}</div>
          </div>
          <NextButton
            v-if="ch.connected"
            slate
            faded
            label="Abrir caixa"
            @click="openInbox(ch)"
          />
          <NextButton
            v-else
            solid
            blue
            :disabled="!ch.allowed || ch.quota_reached"
            :is-loading="connectingId === ch.id"
            label="Conectar"
            @click="connect(ch)"
          />
        </li>
      </ul>
    </template>
  </div>
</template>
